import { format } from "date-fns";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useGReverseGeocodeQuery } from "../../api/network/mapApiServices";
import { setModalsData, TModalsState } from "../../api/store/commonSlice";
import { LegacyOrganizationAccount } from "../../api/types/Account";
import { TLegacyDriver } from "../../api/types/Driver";
import { VehicleMapDataPoint } from "../../api/types/VehicleMap";
import { VehicleStatusCode } from "../../api/types/VehicleStatusCode";
import CrashTrackImpactIcon from "../../assets/svg/crashtrack-impact.svg";
import svgIcons from "../../assets/svg/svgIcons";
import { mapVehicleEventIconWrapped } from "../../assets/svg/vehicle-event-wrapped";
import { mapVehicleIconWrapped } from "../../assets/svg/vehicle-wrapped";
import StatusBadge from "../../components/statusBadge";
import { APP_CONFIG } from "../../constants/constants";
import { TLatLng } from "../../types/map";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
  humanReadableTimeDuration,
} from "../../utils/dateTime";
import {
  accountDistanceUnit,
  convertKmphTo,
  getDistanceByUnit,
  speedUnitSuffix,
} from "../../utils/distance";
import { calcDistanceInKmBetweenTwoPoints } from "../../utils/map";
import { useLoggedInUserData } from "../../utils/user";
import {
  getVehicleEventStatusCodeKey,
  getVehicleEventStatusDisplayName,
} from "../../utils/vehicle";
import {
  DISTANCE_UNIT_VALUES,
  DISTANCE_UNITS,
  DISTANCE_UNITS_OPTIONS,
  SPEED_UNIT_VALUES,
  SPEED_UNITS_OPTIONS,
} from "../admin-details/account/constants";
import { IGNITION_INPUT_OPTIONS } from "../admin-details/vehicle/constants";
import { CrashTrackTabParams } from "../crash-track/type";
import { TRIP_COLORS } from "./constants";
import { TripSummaryChips } from "./tripSummaryChips";
import { ScreenMapAssetBuzzerAlertParams } from "./map-asset";
import { TMapRef } from "./type";
import {
  getTripStartEndOdometerKMWOffset,
  getTripStartEndPoints,
} from "./util/tripPoint";
import {
  formatDuration,
  isMovingEvent,
  isStoppedEvent,
  summariseTrip,
} from "./util/tripSummary";
import { groupIntoRuns } from "./multiDay/eventStacks";

type TripDetailsProps = {
  assignedDriver?: TLegacyDriver;
  detailedView?: boolean;
  onClick: () => void;
  onMouseOver?: () => void;
  onMouseOut?: () => void;
  onReplayEvents?: () => void;
  replayInProgress?: boolean;
  replayPercentage?: number | null;
  selected?: boolean;
  // null for a non-trip: it is not a trip, so it doesn't get a trip number
  tripNumber: number | null;
  tripPoints: VehicleMapDataPoint[];
  selectedTripPoint: VehicleMapDataPoint | null;
  setSelectedTripPoint: (
    value: React.SetStateAction<VehicleMapDataPoint | null>
  ) => void;
  mapRef?: React.RefObject<TMapRef>;
  crashTrackEventData?: CrashTrackTabParams | null;
  // multi-day trip selection: checkbox + swatch colour override (day colour
  // instead of the default per-trip TRIP_COLORS palette)
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  colorOverride?: string;
  // legacy behaviour disables the row's onClick once detailedView is true
  // (nothing to toggle back to, since the parent hides sibling rows
  // instead). the multi-day list keeps every row visible and needs the
  // click to keep working so the user can collapse it again.
  keepClickableInDetailedView?: boolean;
  assetLabel?: string | null;
};

type TripAddressProps = {
  wrapperClassName?: string;
  point: VehicleMapDataPoint;
  pointType: "Start" | "End";
  driver_name?: string;
  phone_no: string;
  address: string;
  latlng: TLatLng;
  time: string;
};

const TripAddress: FC<TripAddressProps> = ({
  wrapperClassName = "relative flex items-start",
  point,
  pointType,
  driver_name = "",
  phone_no,
  address,
  latlng,
  time,
}) => {
  const [fullAddress, setFullAddress] = useState<string>(address);
  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";

  const { data: dataReverseGeocode, isFetching: isFetchingReverseGeocode } =
    useGReverseGeocodeQuery(latlng, {
      skip: !!address || !latlng.latitude || !latlng.longitude,
    });

  useEffect(() => {
    if (
      !!dataReverseGeocode &&
      !isFetchingReverseGeocode &&
      !!dataReverseGeocode?.results?.[0]?.formatted_address
    ) {
      setFullAddress(dataReverseGeocode?.results?.[0]?.formatted_address);
    }
  }, [dataReverseGeocode, isFetchingReverseGeocode]);

  return (
    <div className={wrapperClassName}>
      {pointType === "Start" && (
        <div className="absolute top-6 left-3 ml-[3px] w-0.5 h-[calc(100%-8px)] border border-dashed border-gray-400" />
      )}
      <img
        src={
          pointType === "Start"
            ? point.statuscode === VehicleStatusCode.STATUS_IGNITION_ON
              ? svgIcons.StartLocation
              : svgIcons.UnknownTripLocation
            : point.statuscode === VehicleStatusCode.STATUS_IGNITION_OFF
              ? svgIcons.EndLocation
              : svgIcons.UnknownTripLocation
        }
        alt="location-icon"
        className="p-2"
      />
      <div className="font-medium space-y-1">
        <div className="text-[12px] leading-4 text-heading-gray444 pe-12">
          {showDriver && (!!driver_name || !!phone_no) && (
            <p className="text-accent-blue-dark">
              {/* className={!!phone_no ? "" : "text-gray-400/80"} */}
              {[driver_name, phone_no].filter((item) => !!item).join(" | ")}
            </p>
          )}
          {!!fullAddress && <p>{fullAddress}</p>}
        </div>
        <p className="text-[10px] leading-3 text-heading-gray888">{time}</p>
      </div>
    </div>
  );
};

const TripDetails: FC<TripDetailsProps> = ({
  assignedDriver,
  detailedView = false,
  onClick,
  onMouseOver,
  onMouseOut,
  onReplayEvents,
  replayInProgress,
  replayPercentage,
  selected = false,
  tripNumber,
  tripPoints,
  selectedTripPoint,
  setSelectedTripPoint,
  mapRef,
  crashTrackEventData = null,
  checked,
  onCheckedChange,
  colorOverride,
  keepClickableInDetailedView = false,
  assetLabel=null,
}) => {
  const { buzzerAlertTimestamp } = useParams<ScreenMapAssetBuzzerAlertParams>();
  const { t } = useTranslation("translation", {
    keyPrefix: "vehicleMap.tripDetails",
  });
  const accountData = useSelector(
    (state: { commonReducer: { account: LegacyOrganizationAccount } }) =>
      state.commonReducer.account
  );

  const { tripStartPoint, tripEndPoint } = getTripStartEndPoints(tripPoints);
  const { tripStartOdometerKMWOffset, tripEndOdometerKMWOffset } =
    getTripStartEndOdometerKMWOffset(tripPoints);

  const dispatch = useDispatch();
  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );
  const [tripPointsForList, setTripPointsForList] = useState<
    VehicleMapDataPoint[]
  >(
    tripPoints.filter((tripPoint) => {
      if (!!buzzerAlertTimestamp) {
        return tripPoint.timestamp === parseInt(buzzerAlertTimestamp);
      }
      return true;
    })
  );

  // what happened on this trip, without opening every event in turn
  const { summary, runSecondsByPoint } = useMemo(
    () => summariseTrip(tripPoints),
    [tripPoints]
  );

  const orgSpeedUnitValue = useLoggedInUserData(
    "speedUnits"
  ) as SPEED_UNIT_VALUES;
  const speedUnit =
    SPEED_UNITS_OPTIONS.find((option) => option.value === orgSpeedUnitValue)
      ?.unit ?? SPEED_UNITS_OPTIONS[0].unit;
  const formatSpeed = (speedKph: number) =>
    `${Math.round(convertKmphTo(speedUnit, speedKph))} ${speedUnitSuffix(speedUnit)}`;

  const eventRowLabel = (tripPoint: VehicleMapDataPoint) =>
    tripPoint.resolvedDescription ??
    getVehicleEventStatusDisplayName(
      tripPoint,
      getVehicleEventStatusCodeKey(tripPoint),
      tripPoint?.ignitionindex === parseInt(IGNITION_INPUT_OPTIONS[1].value)
    );

  // shared by an ordinary row and a collapsed run's header, so a run of
  // driving events still shows the heading arrow its members would have
  const eventRowIconHtml = (tripPoint: VehicleMapDataPoint) =>
    isMovingEvent(tripPoint)
      ? mapVehicleIconWrapped("driving", false, tripPoint.heading || 0, 28)
      : !!crashTrackEventData?.assetCrashIdTimestamp &&
          tripPoint.timestamp ===
            parseInt(crashTrackEventData?.assetCrashIdTimestamp) &&
          !!APP_CONFIG.CRASHTRACK.USE_SPL_ICON
        ? CrashTrackImpactIcon(APP_CONFIG.MAPS.VEHICLE_EVENT_ICON_SIZE)
        : mapVehicleEventIconWrapped(tripPoint.statuscode, true);

  // A 4,000-event trip is mostly the same event over and over - a parked
  // asset reporting "Stopped" every six seconds for an hour. Consecutive
  // repeats become one row carrying the count and how long they lasted,
  // and open to the individual events when someone wants them.
  //
  // Unlike the map, driving events collapse here too: the list has no
  // direction to show, so fifty "Driving" rows are just fifty rows.
  const eventRuns = useMemo(
    () =>
      groupIntoRuns(
        tripPointsForList.map((point, pointIndex) => ({ point, pointIndex }))
      ),
    [tripPointsForList]
  );
  const rowKeyFor = (tripPoint: VehicleMapDataPoint) =>
    `${tripPoint.recordid ?? tripPoint.timestamp}`;
  const runKeyFor = (run: (typeof eventRuns)[number]) =>
    `run-${rowKeyFor(run[0].point)}`;
  // to the next thing that happened rather than to the last identical
  // report - the asset was still doing it until something else came in
  const runSecondsFor = (run: (typeof eventRuns)[number]) => {
    const after = tripPointsForList[run[run.length - 1].pointIndex + 1];
    const endsAt = after ? after.timestamp : run[run.length - 1].point.timestamp;
    return Math.max(0, endsAt - run[0].point.timestamp);
  };
  const [expandedRuns, setExpandedRuns] = useState<Set<string>>(new Set());
  const toggleRun = (key: string) =>
    setExpandedRuns((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const runRowRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // the map can pick an event, and the row for it is often well off screen
  // in a trip carrying hundreds. Same treatment the trip lists already give
  // a route hovered on the map.
  const eventRowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const selectedRowKey = selectedTripPoint
    ? rowKeyFor(selectedTripPoint)
    : null;
  useEffect(() => {
    if (!detailedView || !selectedRowKey) return;
    // the event may be folded inside a collapsed run, which has to open
    // before there is a row to scroll to
    const holdingRun = eventRuns.find(
      (run) =>
        run.length > 1 &&
        run.some((item) => rowKeyFor(item.point) === selectedRowKey)
    );
    const holdingKey = holdingRun ? runKeyFor(holdingRun) : null;
    if (holdingKey)
      setExpandedRuns((previous) =>
        previous.has(holdingKey)
          ? previous
          : new Set([...previous, holdingKey])
      );
    // let the row render first - picking an event can open this panel, and
    // the run above, in the same tick
    const timer = setTimeout(() => {
      const target =
        eventRowRefs.current.get(selectedRowKey) ??
        (holdingKey ? runRowRefs.current.get(holdingKey) : undefined);
     
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRowKey, detailedView, eventRuns]);

  const orgDistanceUnit = useLoggedInUserData(
    "distanceUnits"
  ) as DISTANCE_UNIT_VALUES;
  const distanceUnit: DISTANCE_UNITS =
    DISTANCE_UNITS_OPTIONS.find((option) => option.value === orgDistanceUnit)
      ?.unit || DISTANCE_UNITS_OPTIONS[0].unit;
  // this state is disabled as we will directly make use of the detailedView prop
  // const [showEvents, setShowEvents] = useState(detailedView);

  // check if tripPoints don't have ignition on and off status
  const ignitionOnNotPresent = useMemo(() => {
    return tripPoints.every(
      (tripPoint) =>
        tripPoint.statuscode !== VehicleStatusCode.STATUS_IGNITION_ON
    );
  }, [tripPoints]);
  const ignitionOffNotPresent = useMemo(() => {
    return tripPoints.every(
      (tripPoint) =>
        tripPoint.statuscode !== VehicleStatusCode.STATUS_IGNITION_OFF
    );
  }, [tripPoints]);

  // useEffect(() => {
  //   if (ignitionOffNotPresent && !ignitionOnNotPresent) {
  //     setRefreshingTrip(true); // if tripEndPoint is not present, we can set refreshingTrip to true
  //   } else if (
  //     ignitionOffNotPresent &&
  //     fromUnixTime(tripEndPoint?.timestamp).getMilliseconds() +
  //       2 * (60 * 60 * 1000) >
  //       Date.now()
  //   ) {
  //     // If last point was reported less than 2 hours ago, we can set refreshingTrip to true
  //     setRefreshingTrip(true);
  //   }
  // }, [ignitionOffNotPresent, ignitionOnNotPresent, tripEndPoint, detailedView]);

  const renderEventRow = (tripPoint: VehicleMapDataPoint, index: number) => {
                const statusCodeKey = getVehicleEventStatusCodeKey(tripPoint);
                const isMotionStartStopVehicle =
                  tripPoint?.ignitionindex ===
                  parseInt(IGNITION_INPUT_OPTIONS[1].value);
                const handleIconClick = (tripPoint: VehicleMapDataPoint) => {
                  if (!tripPoint) return;
                  if (tripPoint.recordid === selectedTripPoint?.recordid) {
                    setSelectedTripPoint(null);
                    return;
                  }
                  setTimeout(() => {
                    setSelectedTripPoint(tripPoint);
                  }, 100);
                  dispatch(
                    setModalsData({
                      ...modalsState,
                      showLayerFilter: false,
                      showEventFilter: false,
                      showVehicleFilter: false,
                      showVehicleDetails: true,
                    })
                  );
                };
                const rowKey = `${tripPoint.recordid ?? tripPoint.timestamp}`;
                // how long this run of standing still lasted, on every row
                // of that run rather than split between them
                const runSeconds = runSecondsByPoint.get(tripPoint);
                return (
                  <div
                    ref={(element) => {
                      if (element) eventRowRefs.current.set(rowKey, element);
                      else eventRowRefs.current.delete(rowKey);
                    }}
                    className={`flex items-center justify-between py-1 px-2 cursor-pointer rounded-md ${selectedTripPoint?.recordid === tripPoint?.recordid ? "bg-accent-blue-pale" : "hover:bg-accent-blue-paleO66"}`}
                    key={index}
                    title={
                      tripPoint.resolvedDescription ??
                      getVehicleEventStatusDisplayName(tripPoint, statusCodeKey, isMotionStartStopVehicle)
                      // tripPoint?.status?.description ??
                      // getVehicleEventStatusCodeKey(tripPoint)
                    }
                    onClick={() => handleIconClick(tripPoint)}
                  >
                    <div className="flex items-center gap-2">
                      {/* <img src={event.icon} alt="icon"/> */}
                      <div
                        dangerouslySetInnerHTML={{
                          __html: eventRowIconHtml(tripPoint),
                        }}
                      />
                      <div className="min-w-0">
                        <p className="font-medium text-xs leading-4">
                          {tripPoint.resolvedDescription ??
                            getVehicleEventStatusDisplayName(tripPoint, statusCodeKey, isMotionStartStopVehicle)}
                        </p>
                        {/* the one figure that matters for this kind of
                            event, so the row answers itself instead of
                            having to be opened */}
                        {isMovingEvent(tripPoint) ? (
                          <p className="text-[10px] leading-3 text-heading-gray666">
                            {formatSpeed(tripPoint.speedkph)}
                          </p>
                        ) : (
                          !!runSeconds && (
                            <p className="text-[10px] leading-3 text-heading-gray666">
                              {isStoppedEvent(tripPoint)
                                ? t("row_stopped_for")
                                : t("row_idling_for")}{" "}
                              {formatDuration(runSeconds)}
                            </p>
                          )
                        )}
                      </div>
                    </div>
                    <div className="text-right font-medium text-[8px] leading-1 text-heading-gray666">
                      <p>
                        {formattedDateTimeBasedOnTimezone(
                          dbTimeToJsTime(parseInt(`${tripPoint.timestamp}`)),
                          accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE,
                          "LLL dd, yyyy"
                        )}
                      </p>
                      <p className="uppercase">
                        {formattedDateTimeBasedOnTimezone(
                          dbTimeToJsTime(parseInt(`${tripPoint.timestamp}`)),
                          accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE,
                          "hh:mm:ss aa"
                        )}
                      </p>
                    </div>
                  </div>
                );
  };

  // What the row is called, and what state it is in. They are two separate
  // facts - "Trip 2" and "(Completed Trip)" - except for a non-trip, where
  // the name IS the state and printing both read "Non-Trip (Non-Trip)".
  const tripNameLabel =
    tripNumber === null
      ? t("non_trip")
      : t(!!crashTrackEventData ? "incident_trip" : "trip_number", {
          number: tripNumber,
        });
  const tripStateLabel =
    !ignitionOnNotPresent && !ignitionOffNotPresent
      ? t("completed_trip")
      : !ignitionOnNotPresent || !ignitionOffNotPresent
        ? t("partial_trip")
        : !ignitionOnNotPresent &&
            formattedDateTimeBasedOnTimezone(
              dbTimeToJsTime(parseInt(`${tripStartPoint?.timestamp}`)),
              accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE,
              "MM/dd/yyyy (hh:mmaa)"
            ).split(" (")[0] === format(new Date(), "MM/dd/yyyy")
          ? t("active_trip")
          : t("non_trip");

  return (
    <>
      <div
        className={`border-b px-3 py-2 border-gray-200 w-full ${
          selected
            ? "bg-accent-blue-pale"
            : !detailedView || keepClickableInDetailedView
              ? "hover:bg-accent-blue-paleO66 cursor-pointer"
              : ""
        }`}
        onClick={
          detailedView && !keepClickableInDetailedView ? () => {} : onClick
        }
        onMouseOver={detailedView ? () => {} : onMouseOver}
        onMouseOut={detailedView ? () => {} : onMouseOut}
      >
        <div className="space-y-2">
          <div className="flex justify-between">
            <p className="flex gap-2 items-center font-semibold text-base leading-6 text-accent-blue-dark">
              {onCheckedChange && (
                <input
                  type="checkbox"
                  className="size-4 accent-accent-blue-dark cursor-pointer"
                  checked={!!checked}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => onCheckedChange(e.target.checked)}
                  aria-label={
                    tripNumber === null
                      ? "Select non-trip"
                      : `Select trip ${tripNumber}`
                  }
                />
              )}
              <span
                className={`block size-4 rounded-md border border-accent-blue-dark`}
                style={{
                  backgroundColor:
                    colorOverride ?? TRIP_COLORS[(tripNumber ?? 1) - 1],
                }}
              ></span>
              <span className="text-[14px]">
              {tripNameLabel}
              </span>
              {/* {//(tripStartPoint.statuscode !== VehicleStatusCode.STATUS_IGNITION_ON || tripEndPoint.statuscode !== VehicleStatusCode.STATUS_IGNITION_OFF)
                (ignitionOnNotPresent || ignitionOffNotPresent)
                && (
                  <span>
                    {` (${t('partial_trip')})`}
                  </span>
                )
              } */}
              {tripStateLabel !== tripNameLabel && (
                <span className="text-[14px]">{` (${tripStateLabel})`}</span>
              )}
            </p>
            <div className="text-end">
              {/* trip duration */}
              <p className="font-bold text-[8px] leading-2">
                {t("duration")}:{" "}
                {humanReadableTimeDuration(
                  dbTimeToJsTime(
                    (tripEndPoint?.timestamp || 0) -
                      (tripStartPoint?.timestamp || 0)
                  )
                )}
              </p>
              {/* trip distance travelled */}
              <p className="font-bold text-[10px] leading-3 mt-1 text-heading-gray888">
                {`${getDistanceByUnit(
                  tripEndOdometerKMWOffset && tripStartOdometerKMWOffset
                    ? // use odometer readings (with offset) for distance calculation if available,
                      tripEndOdometerKMWOffset - tripStartOdometerKMWOffset
                    : // otherwise fallback to calculating distance using lat/lng of trip start and end points
                      calcDistanceInKmBetweenTwoPoints(
                        {
                          latitude: tripStartPoint?.latitude || 0,
                          longitude: tripStartPoint?.longitude || 0,
                        },
                        {
                          latitude: tripEndPoint?.latitude || 0,
                          longitude: tripEndPoint?.longitude || 0,
                        }
                      ),
                  orgDistanceUnit
                ).toFixed(2)} ${accountDistanceUnit(distanceUnit)}`}
              </p>
            </div>
          </div>
          {assetLabel && (
            <div className="text-xs font-bold mt-1 ml-4">
             Asset: {assetLabel}
            </div>
          )}
          <div className="flex justify-between pt-2">
            <div className="space-y-2">
              {/* trip start and end address with time */}
              <TripAddress
                point={tripStartPoint}
                pointType="Start"
                driver_name={
                  assignedDriver?.description || assignedDriver?.driverid || ""
                }
                phone_no={assignedDriver?.contactphone || ""}
                address={
                  tripStartPoint?.address || tripStartPoint?.address || ""
                }
                latlng={{
                  latitude: tripStartPoint?.latitude || 0,
                  longitude: tripStartPoint?.longitude || 0,
                }}
                time={formattedDateTimeBasedOnTimezone(
                  dbTimeToJsTime(parseInt(`${tripStartPoint?.timestamp}`)),
                  accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE,
                  "MM/dd/yyyy (hh:mmaa)"
                )}
              />
              <TripAddress
                point={tripEndPoint}
                pointType="End"
                driver_name={
                  assignedDriver?.description || assignedDriver?.driverid || ""
                }
                phone_no={assignedDriver?.contactphone || ""}
                address={tripEndPoint?.address || tripEndPoint?.address || ""}
                latlng={{
                  latitude: tripEndPoint?.latitude || 0,
                  longitude: tripEndPoint?.longitude || 0,
                }}
                time={formattedDateTimeBasedOnTimezone(
                  dbTimeToJsTime(parseInt(`${tripEndPoint?.timestamp}`)),
                  accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE,
                  "MM/dd/yyyy (hh:mmaa)"
                )}
              />
            </div>
            {/* {detailedView && (
              <button
                type="button"
                className="font-medium text-sm leading-5 disabled:text-heading-gray888/50 enabled:text-accent-blue-bright hover:text-accent-blue-dark enabled:hover:underline enabled:cursor-pointer"
                onClick={() => setRefreshPaused((state) => !state)}
                disabled={replayInProgress}
              >
                {refreshPaused ? t("resume_update") : t("pause_update")}
              </button>
            )} */}
          </div>
          {/* the trip at a glance. Sits above Total Events in both the
              collapsed card and the expanded detail, since it answers the
              same question in both places. */}
          <TripSummaryChips summary={summary} className="pt-2" />
          <div className="flex justify-between pt-2">
            <p className="flex justify-between font-semibold text-xs leading-6 text-heading-gray666">
              <span>
                {t(!!buzzerAlertTimestamp ? "buzzer_alert" : "total_events")}:{" "}
                <span className="text-heading-black">
                  {tripPointsForList.length}
                </span>
              </span>
            </p>
            {detailedView ? (
              <>
                {!!replayPercentage ? (
                  <div className="flex w-12 h-4 border border-accent-blue-dark rounded-sm">
                    <div
                      className="bg-accent-blue-dark rounded-sm"
                      style={{ width: `${replayPercentage}%` }}
                    ></div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="group flex items-center gap-2 font-medium text-sm leading-5 disabled:text-heading-gray888/50 enabled:text-accent-blue-dark enabled:hover:text-accent-blue-bright whitespace-nowrap enabled:cursor-pointer"
                    onClick={onReplayEvents}
                    disabled={replayInProgress}
                  >
                    <img
                      src={svgIcons.PlayO}
                      alt=""
                      className="w-[17px] h-[18px] group-disabled:grayscale group-disabled:opacity-40"
                    />
                    {t("replay_events")}
                  </button>
                )}
              </>
            ) : (
              <p
                className="font-medium text-sm leading-5 text-accent-blue-bright cursor-pointer"
                // onClick={() => setShowEvents(true)}
              >
                {t("view_details")}
              </p>
            )}
          </div>
          {detailedView && (
            <div className="space-y-1 pb-2">
              {/* onClick={() => setShowEvents(false)} */}
              {eventRuns.map((run) => {
                if (run.length === 1) return renderEventRow(run[0].point, run[0].pointIndex);
                const key = runKeyFor(run);
                const expanded = expandedRuns.has(key);
                const lead = run[run.length - 1].point;
                const holdsSelection = run.some(
                  (item) => rowKeyFor(item.point) === selectedRowKey
                );
                return (
                  <div key={key}>
                    <div
                      ref={(element) => {
                        if (element) runRowRefs.current.set(key, element);
                        else runRowRefs.current.delete(key);
                      }}
                      className={`flex items-center justify-between py-1 px-2 cursor-pointer rounded-md ${holdsSelection && !expanded ? "bg-accent-blue-pale" : "hover:bg-accent-blue-paleO66"}`}
                      onClick={() => toggleRun(key)}
                      title={eventRowLabel(lead)}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          dangerouslySetInnerHTML={{
                            __html: eventRowIconHtml(lead),
                          }}
                        />
                        <div className="min-w-0">
                          <p className="font-medium text-xs leading-4">
                            {eventRowLabel(lead)}{" "}
                            <span className="text-heading-gray666">
                              &times;{run.length}
                            </span>
                          </p>
                          <p className="text-[10px] leading-3 text-heading-gray666">
                            {formatDuration(runSecondsFor(run))}
                          </p>
                        </div>
                      </div>
                      <div className="text-right font-medium text-[8px] leading-1 text-heading-gray666">
                        <p>
                          {formattedDateTimeBasedOnTimezone(
                            dbTimeToJsTime(parseInt(`${run[0].point.timestamp}`)),
                            accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE,
                            "hh:mm:ss aa"
                          )}
                          {" - "}
                          {formattedDateTimeBasedOnTimezone(
                            dbTimeToJsTime(parseInt(`${lead.timestamp}`)),
                            accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE,
                            "hh:mm:ss aa"
                          )}
                        </p>
                        <p className="text-[10px] text-accent-blue-bright">
                          {expanded ? t("hide_events") : t("show_events")}
                        </p>
                      </div>
                    </div>
                    {expanded && (
                      <div className="ps-4 space-y-1">
                        {run.map((item) =>
                          renderEventRow(item.point, item.pointIndex)
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {/* <div id={`itineraryContainer-${tripNumber}`}></div> */}
    </>
  );
};

export default React.memo(TripDetails);
